import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  iTicketingSetup,
  iTicketType,
} from 'services/Ticketing/Ticketing.type';
import { formatShortDay, primaryButton, SwitchField } from '../common';

interface TypeDialogProps {
  type: Partial<iTicketType>;
  data: iTicketingSetup;
  onClose: () => void;
  onSave: (
    input: Omit<iTicketType, 'id' | 'sold' | 'sort_order'>,
    id?: number
  ) => void;
  onDelete: (id: number) => void;
}

export function TypeDialog({
  type,
  data,
  onClose,
  onSave,
  onDelete,
}: TypeDialogProps): JSX.Element {
  const initial: Omit<iTicketType, 'id' | 'sold' | 'sort_order'> = {
    category: type.category ?? 'visitor',
    name_fr: type.name_fr ?? '',
    name_en: type.name_en ?? '',
    description_fr: type.description_fr ?? '',
    description_en: type.description_en ?? '',
    price_cents: type.price_cents ?? 0,
    quota: type.quota ?? null,
    valid_days: type.valid_days ?? [],
    allow_reentry: type.allow_reentry ?? true,
    is_public: type.is_public ?? true,
    is_active: type.is_active ?? true,
  };

  const [v, setV] = useState(initial);
  const [price, setPrice] = useState(
    ((type.price_cents ?? 0) / 100).toFixed(2).replace('.', ',')
  );

  const toggleDay = (d: string): void =>
    setV({
      ...v,
      valid_days: v.valid_days.includes(d)
        ? v.valid_days.filter((x) => x !== d)
        : [...v.valid_days, d].sort(),
    });

  const kind = { setup: 'Montage', event: 'Salon', teardown: 'Démontage' };

  // Only the days of the event are shown; "+" adds build-up / tear-down days
  const before = data.days.filter((d) => d.kind === 'setup');
  const after = data.days.filter((d) => d.kind === 'teardown');
  const outside = (list: typeof before, fromEnd: boolean): number => {
    const picked = list.findIndex((d) => v.valid_days.includes(d.date));
    if (picked < 0) return 0;
    if (fromEnd) {
      const last = list
        .map((d) => v.valid_days.includes(d.date))
        .lastIndexOf(true);
      return last + 1;
    }
    return list.length - picked;
  };
  const defaultExtra = (type.category ?? 'visitor') === 'setup' ? 2 : 0;
  const [extraBefore, setExtraBefore] = useState(() =>
    Math.min(before.length, Math.max(outside(before, false), defaultExtra))
  );
  const [extraAfter, setExtraAfter] = useState(() =>
    Math.min(after.length, Math.max(outside(after, true), defaultExtra))
  );
  const shownDays = [
    ...before.slice(before.length - extraBefore),
    ...data.days.filter((d) => d.kind === 'event'),
    ...after.slice(0, extraAfter),
  ];
  /**
   * Choosing the type of a new ticket fills its usual values (name, public or
   * not, days); an existing ticket only changes its type.
   */
  const chooseCategory = (category: iTicketType['category']): void => {
    if (type.id) {
      setV({ ...v, category });
      return;
    }
    const labels = Object.values(data.categories);
    const untouched =
      v.name_fr === '' ||
      labels.includes(v.name_fr) ||
      v.name_fr === 'Pass visiteur';
    setV({
      ...v,
      category,
      name_fr: untouched
        ? category === 'visitor'
          ? 'Pass visiteur'
          : data.categories[category]
        : v.name_fr,
      is_public: category === 'visitor',
      valid_days:
        category === 'setup'
          ? data.days.filter((d) => d.kind !== 'event').map((d) => d.date)
          : category === 'organiser'
          ? data.days.map((d) => d.date)
          : [],
    });
    if (category === 'setup' || category === 'organiser') {
      setExtraBefore(Math.min(before.length, 2));
      setExtraAfter(Math.min(after.length, 2));
    }
  };

  const addTile = {
    cursor: 'pointer',
    px: 1.2,
    py: 0.8,
    borderRadius: 1,
    border: '1px dashed #bbb',
    bgcolor: 'transparent',
    color: '#555',
    fontSize: 12,
    fontFamily: 'inherit',
    lineHeight: 1.4,
    '&:hover': { borderColor: '#111', color: '#111' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  } as const;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {type.id
          ? `Modifier « ${type.name_fr ?? ''} »`
          : 'Nouveau type de billet'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            Type de billet
          </Typography>
          <Box
            role="radiogroup"
            aria-label="Type de billet"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                sm: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {Object.entries(data.categories).map(([k, label]) => {
              const on = v.category === k;
              return (
                <Box
                  key={k}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => chooseCategory(k as iTicketType['category'])}
                  sx={{
                    cursor: 'pointer',
                    py: 1.2,
                    px: 1,
                    borderRadius: '8px',
                    border: on ? '2px solid #111' : '1px solid #ddd',
                    bgcolor: on ? '#111' : '#fff',
                    color: on ? '#fff' : '#111',
                    fontSize: 13,
                    fontWeight: on ? 600 : 500,
                    fontFamily: 'inherit',
                    '&:hover': { borderColor: '#111' },
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField
            size="small"
            label="Nom (français)"
            placeholder="Pass 1 jour – mardi"
            value={v.name_fr}
            onChange={(e) => setV({ ...v, name_fr: e.target.value })}
          />
          <TextField
            size="small"
            label="Nom (anglais)"
            value={v.name_en}
            onChange={(e) => setV({ ...v, name_en: e.target.value })}
          />
          <TextField
            size="small"
            multiline
            label="Description (français)"
            value={v.description_fr}
            onChange={(e) => setV({ ...v, description_fr: e.target.value })}
          />
          <TextField
            size="small"
            multiline
            label="Description (anglais)"
            value={v.description_en}
            onChange={(e) => setV({ ...v, description_en: e.target.value })}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Jours valables{' '}
            <span style={{ fontWeight: 400, color: '#888' }}>
              (aucun = tous les jours du salon)
            </span>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Box
              component="button"
              type="button"
              disabled={extraBefore >= before.length}
              onClick={() => setExtraBefore((n) => n + 1)}
              sx={addTile}
              title="Ajouter un jour avant le salon (montage)"
            >
              <b>+ 1 jour</b>
              <br />
              avant
            </Box>
            {shownDays.map((d) => {
              const on = v.valid_days.includes(d.date);
              return (
                <Box
                  key={d.date}
                  role="button"
                  tabIndex={0}
                  aria-pressed={on}
                  onClick={() => toggleDay(d.date)}
                  onKeyDown={(e) => e.key === 'Enter' && toggleDay(d.date)}
                  sx={{
                    cursor: 'pointer',
                    px: 1.2,
                    py: 0.8,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: on ? '#111' : '#ddd',
                    bgcolor: on
                      ? '#111'
                      : d.kind === 'event'
                      ? '#fff'
                      : '#f6f6f6',
                    color: on ? '#fff' : '#111',
                    fontSize: 12,
                  }}
                >
                  <b>{formatShortDay(d.date)}</b>
                  <br />
                  {kind[d.kind]}
                </Box>
              );
            })}
            <Box
              component="button"
              type="button"
              disabled={extraAfter >= after.length}
              onClick={() => setExtraAfter((n) => n + 1)}
              sx={addTile}
              title="Ajouter un jour après le salon (démontage)"
            >
              <b>+ 1 jour</b>
              <br />
              après
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="Prix TTC"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              setV({
                ...v,
                price_cents:
                  Math.round(
                    parseFloat(e.target.value.replace(',', '.') || '0') * 100
                  ) || 0,
              });
            }}
          />
          <TextField
            size="small"
            type="number"
            label="Quota (vide = illimité)"
            value={v.quota ?? ''}
            onChange={(e) =>
              setV({
                ...v,
                quota: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
          <SwitchField
            label="Proposé au public"
            checked={v.is_public}
            onChange={(value) => setV({ ...v, is_public: value })}
          />
          <SwitchField
            label="Ré-entrées le même jour"
            checked={v.allow_reentry}
            onChange={(value) => setV({ ...v, allow_reentry: value })}
          />
          <SwitchField
            label="Actif"
            checked={v.is_active}
            onChange={(value) => setV({ ...v, is_active: value })}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        {type.id ? (
          <Button
            color="error"
            sx={{ textTransform: 'none' }}
            onClick={() => {
              if (window.confirm('Supprimer ce type de billet ?'))
                onDelete(type.id as number);
            }}
          >
            Supprimer
          </Button>
        ) : (
          <span />
        )}
        <Box>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            disabled={!v.name_fr}
            sx={primaryButton}
            onClick={() => onSave(v, type.id)}
          >
            Enregistrer
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default TypeDialog;
